import React, { useEffect, useState } from "react";
import { makeStyles, Typography, Grid, Tooltip } from "@material-ui/core";

import { PAGES } from "../../pages";
import { COLORS } from "../../theme/colors";
import { Icon } from "../../components/base";
import MenuIcon from "../../assets/images/icons/menu.svg";
import SearchIcon from "../../assets/images/icons/search.svg";
import useDebounce from "../../hooks/useDebounce";
import { getData } from "../../services";
import { LoadingCircle } from "../loadingCircle";

const useStyles = makeStyles((theme) => ({
  leftExpandedCol: {
    height: "100%",
    backgroundColor: COLORS.darks[6],
    position: "absolute",
    zIndex: 1000,
  },
  animatedLeftExpandedCol: {
    transition: "width 0.5s",
    "& p": {
      whiteSpace: "noWrap",
    },
  },
  menuIcon: {
    margin: `${theme.spacing(2)}px ${theme.spacing(0.8)}px`,
  },
  searchContainer: {
    margin: theme.spacing(2),
  },
  searchInput: {
    width: "95%",
    backgroundColor: "transparent",
    border: "none",
    borderBottom: `1px solid ${COLORS.darks[0][50]}`,
    "&:focus-visible": {
      outline: "none",
    },
  },
  allBuildingTitle: {
    fontWeight: "bold",
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingLeft: 16,
    cursor: "pointer",
  },
  buildingContainer: {
    borderTop: `solid 1px ${COLORS.darks[0][10]}`,
    overflowY: "scroll",
    overflowX: "hidden",
    height: "100vh",
  },
  buildingRow: {
    padding: `${theme.spacing(0.8)}px ${theme.spacing(2)}px`,
    borderTop: `solid 1px ${COLORS.darks[0][10]}`,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: COLORS.whites[75],
    },
  },
  address: {
    lineHeight: 2.5,
    marginLeft: theme.spacing(1),
    color: COLORS.darks[1],
  },
  activeRow: {
    backgroundColor: COLORS.whites[75],
    borderLeft: "4px solid rgba(193, 37, 173, 0.75)",
    paddingLeft: 12,
  },
}));

const ItemRow = ({ active, setMenuOpen, url, name, tooltipComponent, navigate }) => {
  const classes = useStyles();

  return (
    <Grid
      className={`${classes.buildingRow} ${active ? classes.activeRow : null}`}
      container
      onClick={() => {
        navigate(url);
        setMenuOpen(false);
      }}
    >
      <Typography style={{ fontWeight: "bold" }}>{name}</Typography>
      <Typography
        style={{
          color: COLORS.darks[1],
          width: "250px",
          overflow: "hidden",
        }}
      >
        {tooltipComponent}
      </Typography>
    </Grid>
  );
};

const Menu = ({ navigate, buildings, complexes, selectedBuilding, selectedComplex }) => {
  const classes = useStyles();
  const [menuOpen, setMenuOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [searchedComplexes, setSearchedComplexes] = useState();
  const [searchedBuildings, setSearchedBuildings] = useState();
  const [searching, setSearching] = useState(false);

  const searchDebounce = useDebounce(search);

  useEffect(() => {
    if (searchDebounce) {
      setSearching(true);

      getData(
        "/praas/v2/complexes/",
        (complexes) => {
          setSearchedComplexes(complexes);
        },
        { search: searchDebounce }
      );

      getData(
        "/praas/v2/buildings/without-complex/",
        (buildings) => {
          setSearchedBuildings(buildings);
        },
        { search: searchDebounce }
      );

      setSearching(false);
    } else {
      setSearchedComplexes();
      setSearchedBuildings();
    }
  }, [searchDebounce]);

  const ComplexesBuildingsContainer = ({ complexes, buildings }) => {
    return (
      <>
        {complexes?.map((complex) => (
          <ItemRow
            key={complex.id}
            active={complex.id == selectedComplex}
            url={`${PAGES.buildings.index}/${PAGES.buildings.complexes}/${complex.id}`}
            name={complex.name}
            tooltipComponent={
              <Tooltip
                arrow
                title={
                  <React.Fragment>
                    {complex.addresses.map((address, index) => (
                      <div key={index}>{address}</div>
                    ))}
                  </React.Fragment>
                }
              >
                <span>{complex.addresses[0] ?? "-"}</span>
              </Tooltip>
            }
            navigate={navigate}
          />
        ))}
        {buildings?.map((building, index) => (
          <ItemRow
            key={index}
            active={building.id == selectedBuilding}
            url={`${PAGES.buildings.index}/${building.id}`}
            name={building.name}
            tooltipComponent={
              <Tooltip arrow title={building.address}>
                <span>{building.address}</span>
              </Tooltip>
            }
            navigate={navigate}
          />
        ))}
      </>
    );
  };

  return (
    <div
      className={classes.leftExpandedCol}
      style={{ boxShadow: menuOpen ? "4px 0px 4px 0px #0000001A" : null }}
    >
      <Icon
        root={classes.menuIcon}
        src={MenuIcon}
        hoverable
        onClick={() => setMenuOpen(!menuOpen)}
      />

      <div
        className={classes.animatedLeftExpandedCol}
        style={{
          width: menuOpen ? 300 : 0,
        }}
      >
        {menuOpen && (
          <>
            <Grid className={classes.searchContainer} container>
              <Grid item xs={10}>
                <input
                  className={classes.searchInput}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Grid>
              <Grid item xs={2}>
                <Icon src={SearchIcon} hoverable />
              </Grid>
            </Grid>

            {searching ? (
              <LoadingCircle />
            ) : searchedComplexes?.length === 0 && searchedBuildings?.length === 0 ? (
              <Typography align="center">No buildings found</Typography>
            ) : searchedComplexes || searchedBuildings ? (
              <div className={classes.buildingContainer}>
                <ComplexesBuildingsContainer
                  complexes={searchedComplexes}
                  buildings={searchedBuildings}
                />
              </div>
            ) : (
              <div className={classes.buildingContainer}>
                <Grid
                  className={`${classes.allBuildingTitle} ${
                    !selectedBuilding && !selectedComplex ? classes.activeRow : null
                  }`}
                  onClick={() => {
                    navigate(`${PAGES.buildings.index}`);
                    setMenuOpen(false);
                  }}
                >
                  All Buildings
                </Grid>
                <ComplexesBuildingsContainer complexes={complexes} buildings={buildings} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Menu;
